/**
 * ReleasesPage
 *
 * Admin page for viewing and managing application releases.
 * Groups releases by version and displays download links for each platform.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, AppRelease } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useToast } from "@/components/ui/Toast";
import ConfirmModal from "@/components/ui/ConfirmModal";
import {
  Download,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Apple,
  Monitor,
  Server,
  Package,
  ExternalLink,
} from "lucide-react";

// Platform display configuration
const platformConfig: Record<string, { label: string; icon: typeof Apple; shortLabel: string }> = {
  "darwin-aarch64": { label: "macOS (Apple Silicon)", icon: Apple, shortLabel: "macOS ARM" },
  "darwin-x86_64": { label: "macOS (Intel)", icon: Apple, shortLabel: "macOS Intel" },
  "windows-x86_64": { label: "Windows (64-bit)", icon: Monitor, shortLabel: "Windows" },
  "linux-x86_64": { label: "Linux (64-bit)", icon: Server, shortLabel: "Linux" },
};

/**
 * Group releases by version
 */
function groupByVersion(releases: AppRelease[]): Record<string, AppRelease[]> {
  return releases.reduce(
    (acc, release) => {
      if (!acc[release.version]) acc[release.version] = [];
      acc[release.version].push(release);
      return acc;
    },
    {} as Record<string, AppRelease[]>
  );
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: string | null): string {
  if (!bytes) return "-";
  const size = BigInt(bytes);
  const mb = Number(size) / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export default function ReleasesPage() {
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pendingToggle, setPendingToggle] = useState<AppRelease | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  // Fetch releases
  const { data, isLoading, error } = useQuery({
    queryKey: ["releases", page],
    queryFn: () => api.getReleases({ page, pageSize: 100 }),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ["releaseStats"],
    queryFn: api.getReleaseStats,
  });

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.updateRelease(id, { active }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["releases"] });
      queryClient.invalidateQueries({ queryKey: ["releaseStats"] });
      toast.success(
        variables.active ? "已啟用此版本，將開放自動更新分發" : "已停用此版本，將停止自動更新分發"
      );
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "更新版本狀態失敗，請重試");
    },
  });

  const platformLabel = (platform: string) =>
    platformConfig[platform]?.label ?? platform;

  const handleConfirmToggle = () => {
    if (!pendingToggle) return;
    toggleMutation.mutate({ id: pendingToggle.id, active: !pendingToggle.active });
    setPendingToggle(null);
  };

  // Handle download click
  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const { url } = await api.getReleaseDownloadUrl(id);
      window.open(url, "_blank");
    } catch (err) {
      console.error("Failed to get download URL:", err);
      toast.error("無法取得下載連結，請稍後再試");
    } finally {
      setDownloadingId(null);
    }
  };

  // Group releases by version
  const groupedReleases = data ? groupByVersion(data.data) : {};
  const versions = Object.keys(groupedReleases).sort((a, b) =>
    b.localeCompare(a, undefined, { numeric: true })
  );

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-ceramic-900">版本管理</h1>
          <p className="text-ceramic-500 mt-1">查看所有已發布的應用程式版本</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-ceramic-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-ceramic-500">總發布數</div>
                <div className="text-xl font-semibold text-ceramic-900">{stats.totalReleases}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-ceramic-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-sm text-ceramic-500">啟用中</div>
                <div className="text-xl font-semibold text-ceramic-900">{stats.activeReleases}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-ceramic-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Server className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-sm text-ceramic-500">支援平台</div>
                <div className="text-xl font-semibold text-ceramic-900">{stats.platforms.length}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-ceramic-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ExternalLink className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-sm text-ceramic-500">最新版本</div>
                <div className="text-xl font-semibold text-ceramic-900">
                  {stats.latestVersion ?? "-"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Releases List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-ceramic-400" />
        </div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">載入失敗，請重試</div>
      ) : versions.length === 0 ? (
        <div className="bg-white border border-ceramic-200 rounded-xl">
          <div className="text-center py-20 text-ceramic-500">
            <Package className="w-12 h-12 mx-auto mb-4 text-ceramic-300" />
            <p>尚無版本紀錄</p>
            <p className="text-sm mt-2">使用 <code className="bg-ceramic-100 px-2 py-1 rounded">pnpm publish-release</code> 發布新版本</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {versions.map((version) => {
            const versionReleases = groupedReleases[version];
            const firstRelease = versionReleases[0];

            return (
              <div
                key={version}
                className="bg-white border border-ceramic-200 rounded-xl overflow-hidden"
              >
                {/* Version Header */}
                <div className="px-6 py-4 bg-ceramic-50 border-b border-ceramic-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-semibold text-ceramic-900">v{version}</span>
                    {firstRelease.critical && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-sm rounded">
                        <AlertTriangle className="w-3 h-3" />
                        重要更新
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-ceramic-500">{formatDate(firstRelease.pubDate)}</span>
                </div>

                {/* Release Notes */}
                {firstRelease.notes && (
                  <div className="px-6 py-4 border-b border-ceramic-100">
                    <div className="prose prose-sm max-w-none text-ceramic-600">
                      <pre className="whitespace-pre-wrap font-sans text-sm bg-transparent p-0 m-0 max-h-60 overflow-y-auto">
                        {firstRelease.notes}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Platform Downloads */}
                <div className="divide-y divide-ceramic-100">
                  {versionReleases.map((release) => {
                    const config = platformConfig[release.platform] || {
                      label: release.platform,
                      icon: Server,
                      shortLabel: release.platform,
                    };
                    const Icon = config.icon;

                    return (
                      <div
                        key={release.id}
                        className="px-6 py-3 flex items-center justify-between hover:bg-ceramic-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 text-ceramic-500" />
                          <span className="text-sm font-medium text-ceramic-700">{config.label}</span>
                          <span className="text-xs text-ceramic-400">
                            {formatFileSize(release.fileSizeBytes)}
                          </span>
                        </div>

                        <div className="flex items-center gap-3">
                          {/* Active Status Toggle */}
                          <button
                            onClick={() => setPendingToggle(release)}
                            disabled={toggleMutation.isPending}
                            className={cn(
                              "flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors",
                              release.active
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-ceramic-100 text-ceramic-500 hover:bg-ceramic-200"
                            )}
                          >
                            {release.active ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                啟用中
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3" />
                                已停用
                              </>
                            )}
                          </button>

                          {/* Download Button */}
                          <button
                            onClick={() => handleDownload(release.id)}
                            disabled={downloadingId === release.id}
                            className="flex items-center gap-2 px-3 py-1.5 bg-ceramic-900 text-white text-sm rounded-lg hover:bg-ceramic-800 transition-colors disabled:opacity-50"
                          >
                            {downloadingId === release.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            下載
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-ceramic-500">
            共 {data.total} 筆，第 {page} / {data.totalPages} 頁
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page === data.totalPages}
              className="p-2 border border-ceramic-200 rounded-lg hover:bg-ceramic-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Active toggle confirmation */}
      <ConfirmModal
        open={pendingToggle !== null}
        title={pendingToggle?.active ? "停用此版本？" : "啟用此版本？"}
        destructive={pendingToggle?.active ?? false}
        confirmLabel={pendingToggle?.active ? "停用" : "啟用"}
        loading={toggleMutation.isPending}
        description={
          pendingToggle ? (
            pendingToggle.active ? (
              <>
                停用後，使用 <strong>{platformLabel(pendingToggle.platform)}</strong> 的用戶將無法透過自動更新取得{" "}
                <strong>v{pendingToggle.version}</strong>。此操作會立即影響線上分發。
              </>
            ) : (
              <>
                啟用後，<strong>v{pendingToggle.version}</strong>（
                {platformLabel(pendingToggle.platform)}）將成為可被自動更新分發的版本，立即影響線上用戶。
              </>
            )
          ) : null
        }
        onConfirm={handleConfirmToggle}
        onCancel={() => setPendingToggle(null)}
      />
    </div>
  );
}
